import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useSubscription, FeatureInfo, ProFeatures } from '@/context/SubscriptionContext';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import {
  Crown, Sparkles, Scale, Infinity, Columns2, ScanFace,
  Network, FileText, Brain, Map, Check, Loader2, Zap
} from 'lucide-react';

// Icon mapping
const iconMap = {
  Scale: Scale,
  Infinity: Infinity,
  Columns: Columns2,
  ScanFace: ScanFace,
  Network: Network,
  FileText: FileText,
  Brain: Brain,
  Map: Map
};

// All Pro features list
const PRO_FEATURES_LIST = [
  { id: ProFeatures.UNLIMITED_MATCHES, name: 'Unlimited Match Viewing', icon: Infinity },
  { id: ProFeatures.FULL_MATCH_ENGINE, name: 'Full Match Engine', icon: Scale },
  { id: ProFeatures.CASE_COMPARISON, name: 'Case Comparison Tools', icon: Columns2 },
  { id: ProFeatures.FACE_SEARCH, name: 'Face Search', icon: ScanFace },
  { id: ProFeatures.NETWORK_GRAPH, name: 'Network Graph', icon: Network },
  { id: ProFeatures.CASE_PACK_PDF, name: 'Case Pack PDF Export', icon: FileText },
  { id: ProFeatures.INTEL_EXTRACTION, name: 'AI Intel Extraction', icon: Brain },
  { id: ProFeatures.ADVANCED_MAP, name: 'Advanced Map Features', icon: Map },
];

export function TrialModal() {
  const { showTrialModal, closeModals, startTrial, blockedFeature } = useSubscription();
  const [loading, setLoading] = useState(false);

  const handleStartTrial = async () => {
    setLoading(true);
    const result = await startTrial();
    setLoading(false);
    
    if (result.success) {
      toast.success(result.message);
      closeModals();
    } else {
      toast.error(result.message);
    }
  };

  const featureInfo = blockedFeature ? FeatureInfo[blockedFeature] : null;

  return (
    <Dialog open={showTrialModal} onOpenChange={closeModals}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader className="text-center pb-2">
          <div className="mx-auto w-16 h-16 bg-gradient-to-br from-orange-400 to-orange-600 rounded-2xl flex items-center justify-center mb-4">
            <Sparkles className="w-8 h-8 text-white" />
          </div>
          <DialogTitle className="text-2xl font-bold">
            Start Your 7-Day Pro Trial
          </DialogTitle>
          <DialogDescription className="text-base mt-2">
            {featureInfo ? (
              <>Experience <span className="font-semibold text-orange-600">{featureInfo.name}</span> and all Pro features free for 7 days</>
            ) : (
              <>Unlock all advanced investigative tools free for 7 days</>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Feature list */}
          <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-4">
            <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
              Your trial includes:
            </p>
            <div className="grid grid-cols-2 gap-2">
              {PRO_FEATURES_LIST.map(feature => (
                <div key={feature.id} className="flex items-center gap-2 text-sm">
                  <Check className="w-4 h-4 text-green-500 shrink-0" />
                  <span className="text-slate-600 dark:text-slate-400">{feature.name}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Trial info */}
          <div className="flex items-center gap-3 p-3 bg-orange-50 dark:bg-orange-950/30 rounded-lg border border-orange-200 dark:border-orange-800">
            <Zap className="w-5 h-5 text-orange-500" />
            <div className="text-sm">
              <p className="font-medium text-orange-800 dark:text-orange-200">No credit card required</p>
              <p className="text-orange-600 dark:text-orange-400">Full access to all Pro features for 7 days</p>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-3 pt-2">
          <Button 
            onClick={handleStartTrial}
            disabled={loading}
            className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white h-12 text-base font-semibold"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
            ) : (
              <Sparkles className="w-5 h-5 mr-2" />
            )}
            Start Free Trial
          </Button>
          <Button variant="ghost" onClick={closeModals} className="text-slate-500">
            Maybe Later
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function UpgradeModal() {
  const { showUpgradeModal, closeModals, blockedFeature, trialDaysRemaining } = useSubscription();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [billingPeriod, setBillingPeriod] = useState('monthly');

  const featureInfo = blockedFeature ? FeatureInfo[blockedFeature] : null;

  const handleUpgrade = async () => {
    setLoading(true);
    try {
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
      const originUrl = window.location.origin;
      
      // Store context for post-payment redirect
      localStorage.setItem('upgradeContext', JSON.stringify({
        returnUrl: window.location.pathname,
        timestamp: Date.now()
      }));
      
      const response = await fetch(`${API}/subscriptions/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          plan_id: 'pro',
          billing_period: billingPeriod,
          origin_url: originUrl,
          // Send the authenticated user's email so the backend can
          // determine trial-eligibility (first-touch only) and pre-fill
          // the Stripe Checkout customer record.
          email: user?.email || undefined,
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create checkout session');
      }

      const data = await response.json();
      // Redirect to Stripe checkout
      window.location.href = data.url;
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Failed to start checkout. Please try again.');
      setLoading(false);
    }
  };

  return (
    <Dialog open={showUpgradeModal} onOpenChange={closeModals}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader className="text-center pb-2">
          <div className="mx-auto w-16 h-16 bg-gradient-to-br from-amber-400 to-amber-600 rounded-2xl flex items-center justify-center mb-4">
            <Crown className="w-8 h-8 text-white" />
          </div>
          <DialogTitle className="text-2xl font-bold">
            Upgrade to Pro
          </DialogTitle>
          <DialogDescription className="text-base mt-2">
            {featureInfo ? (
              <>
                <span className="font-semibold text-amber-600">{featureInfo.name}</span> is a Pro feature.
                <br />
                Upgrade to continue using advanced investigative tools.
              </>
            ) : (
              <>Unlock unlimited access to all advanced investigative tools</>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Trial expired notice */}
          <div className="flex items-center gap-3 p-3 bg-slate-100 dark:bg-slate-800 rounded-lg">
            <div className="w-10 h-10 bg-slate-200 dark:bg-slate-700 rounded-full flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-slate-500" />
            </div>
            <div className="text-sm">
              <p className="font-medium text-slate-700 dark:text-slate-300">Your trial has ended</p>
              <p className="text-slate-500">Upgrade to Pro for continued access</p>
            </div>
          </div>

          {/* Feature list */}
          <div className="bg-amber-50 dark:bg-amber-950/30 rounded-xl p-4 border border-amber-200 dark:border-amber-800">
            <p className="text-sm font-medium text-amber-800 dark:text-amber-200 mb-3">
              Pro includes everything:
            </p>
            <div className="grid grid-cols-2 gap-2">
              {PRO_FEATURES_LIST.map(feature => (
                <div key={feature.id} className="flex items-center gap-2 text-sm">
                  <Check className="w-4 h-4 text-amber-500 shrink-0" />
                  <span className="text-amber-700 dark:text-amber-300">{feature.name}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Billing period selector */}
          <div className="flex justify-center gap-2 py-2">
            <Button
              variant={billingPeriod === 'monthly' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setBillingPeriod('monthly')}
              className={billingPeriod === 'monthly' ? 'bg-amber-500 hover:bg-amber-600' : ''}
            >
              Monthly
            </Button>
            <Button
              variant={billingPeriod === 'yearly' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setBillingPeriod('yearly')}
              className={billingPeriod === 'yearly' ? 'bg-amber-500 hover:bg-amber-600' : ''}
            >
              Yearly
              <Badge className="ml-2 bg-green-500 text-white text-xs">Save 35%</Badge>
            </Button>
          </div>

          {/* Pricing preview */}
          <div className="text-center py-2">
            <p className="text-3xl font-bold text-slate-900 dark:text-white">
              {billingPeriod === 'monthly' ? (
                <>$19<span className="text-lg font-normal text-slate-500">/month</span></>
              ) : (
                <>$149<span className="text-lg font-normal text-slate-500">/year</span></>
              )}
            </p>
            {billingPeriod === 'yearly' && (
              <p className="text-sm text-green-600 mt-1">That's just $12.42/month!</p>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-3 pt-2">
          <Button 
            onClick={handleUpgrade}
            disabled={loading}
            className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white h-12 text-base font-semibold"
            data-testid="upgrade-pro-btn"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            ) : (
              <Crown className="w-5 h-5 mr-2" />
            )}
            {loading ? 'Redirecting to checkout...' : 'Upgrade to Pro'}
          </Button>
          <Button variant="ghost" onClick={closeModals} className="text-slate-500">
            Maybe Later
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Combined component for easy import
export function SubscriptionModals() {
  return (
    <>
      <TrialModal />
      <UpgradeModal />
    </>
  );
}

export default SubscriptionModals;
